import React, { useEffect, useRef } from 'react';
import { Row, Container, Spinner } from 'react-bootstrap';
import useOrderApi from '../../managers/OrderManager';
import { useNavigate } from 'react-router-dom';
import { useShoppingCart } from '../../context/ShoppingCartContext';
import usePaymentApi from '../../managers/PaymentManager';
import { useAuth0 } from '@auth0/auth0-react';

const Success: React.FC = () => {
  const { user, isLoading, isAuthenticated } = useAuth0();
  const { createOrder, submitOrder } = useOrderApi();
  const navigate = useNavigate();
  const { clearCart, cartItems } = useShoppingCart();
  const { getPayment, clearPayment } = usePaymentApi();

  const isSubmitting = useRef(false); // Ref to track submission state

  useEffect(() => {
    // Wait for Auth0 to finish loading before proceeding
    if (isLoading) {
      console.log('Auth0 still loading, waiting...');
      return;
    }

    console.log('Auth0 loaded - User:', user);
    console.log('Auth0 loaded - Is authenticated:', isAuthenticated);

    const submitOrderAfterPayment = async () => {
      if (isSubmitting.current) return; // Prevent multiple submissions
      isSubmitting.current = true; // Mark as submitting to prevent further submissions

      try {
        // Validate payment session
        const payment = await getPayment();
        if (!payment.paymentSessionId) {
          throw new Error('Payment Session Id not found');
        }

        let initialOrder = await createOrder(cartItems, payment);

        // Submit the order using the payment session
        const submittedOrder = await submitOrder(initialOrder);
        if (!submittedOrder) {
          throw new Error('Error submitting order');
        }

        console.log('Order successfully submitted:', submittedOrder);

        // Navigate to the order confirmation page
        navigate(`/order/${submittedOrder.orderId}`, { state: { orderId: submittedOrder.orderId } });
      } catch (error: any) {
        console.error('Error submitting order:', error?.message);
        navigate('/order/error', { state: { error: error?.message } })
      } finally {
        // clear the cart since we know the payment went through at this point
        clearCart();
        clearPayment();
        isSubmitting.current = false;
      }
    };

    submitOrderAfterPayment();
  }, [isLoading, user, isAuthenticated]);

  return (
    <Container fluid className="min-vh-100 d-flex align-items-center justify-content-center px-3">
      <Row className="w-100 justify-content-center">
        <div className="col-12 col-md-8 col-lg-6 text-center">
          <div className="p-4">
            <h1 className="display-6 mb-4 text-success">Payment Successful</h1>
            <p className="lead mb-4">
              Your payment has been processed successfully. We're now submitting your order to fulfillment...
            </p>
            <div className="d-flex justify-content-center">
              <Spinner animation="border" role="status" className="text-primary">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          </div>
        </div>
      </Row>
    </Container>
  );
};

export default Success;
